<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <nav class="sticky top-0 z-50 px-6 py-3 bg-white text-dark lg:px-24 shadow-md">
    <div class="flex items-center h-20 justify-between">
      <div class="flex items-center">
        <router-link to="/" class="flex items-center">
          <div class="flex flex-col justify-center text-end border-r border-primary pr-2 sm:pr-3 mr-2 sm:mr-3">
            <span class="text-[10px] xs:text-xs uppercase tracking-wider font-semibold text-primary">{{ $t('navbar.by') }}</span>
            <span class="text-[11px] xs:text-xs sm:text-sm font-medium">Hnoud Production</span>
          </div>
          <img src="/logo.png" alt="Logo" class="w-24 sm:w-24 md:w-24 h-full" />
        </router-link>
      </div>
      <div class="flex items-center">
        <!-- Language Switcher positioned before the menu button on mobile -->
        <div class="mr-4 lg:hidden">
          <LanguageSwitcher />
        </div>
        <div class="lg:hidden">
          <button @click="toggleMenu" class="text-dark focus:outline-none">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16m-7 6h7"></path>
            </svg>
          </button>
        </div>
      </div>
      <div class="hidden lg:flex lg:items-center">
        <div class="flex items-center lg:gap-8 lg:text-md mr-6">
          <div
            v-for="link in links"
            :key="link.key"
            class="relative px-2 py-1 rounded transition-colors duration-100 group"
            :class="link.isDropdown || link.isNew ? '' : 'hover:bg-primary'"
            @mouseenter="link.isDropdown && (isDropdownOpen = true)"
            @mouseleave="link.isDropdown && scheduleHideDropdown()"
          >
            <!-- Dropdown parent button -->
            <button
              v-if="link.isDropdown"
              type="button"
              class="flex items-center gap-1.5 font-semibold whitespace-nowrap"
            >
              <Sparkles class="w-4 h-4 text-secondary shrink-0 talent-sparkle" />
              <span class="shiny-text">{{ $t(link.translationKey) }}</span>
              <svg
                class="w-3.5 h-3.5 text-primary transition-transform duration-200"
                :class="{ 'rotate-180': isDropdownOpen }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>

            <!-- Regular link -->
            <a
              v-else
              :href="link.link"
              class="block w-full h-full whitespace-nowrap"
              :class="link.isNew ? 'flex items-center gap-1.5 font-semibold' : 'group-hover:text-white'"
            >
              <Sparkles v-if="link.isNew" class="w-4 h-4 text-secondary shrink-0 talent-sparkle" />
              <span :class="link.isNew ? 'shiny-text' : ''">{{ $t(link.translationKey) }}</span>
              <span
                v-if="link.isNew"
                class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-primary text-white animate-pulse"
              >
                {{ $t('navbar.new') }}
              </span>
            </a>

            <!-- Programs dropdown panel -->
            <div
              v-if="link.isDropdown"
              class="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-64 z-10 transition-all duration-200 ease-out origin-top"
              :class="{ 'opacity-0 invisible scale-95 pointer-events-none': !isDropdownOpen, 'opacity-100 visible scale-100': isDropdownOpen }"
              @mouseenter="isDropdownOpen = true"
              @mouseleave="scheduleHideDropdown"
            >
              <div class="bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                <a
                  v-for="item in programsSubmenu"
                  :key="item.key"
                  :href="item.link"
                  class="flex items-center justify-between px-4 py-3 text-sm text-dark hover:bg-primary hover:text-white transition-colors duration-150"
                >
                  <span class="font-medium">{{ $t(item.translationKey) }}</span>
                  <span
                    v-if="item.isNew"
                    class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-primary text-white"
                  >
                    {{ $t('navbar.new') }}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
        <!-- Language Switcher integrated in the navbar -->
        <LanguageSwitcher />
      </div>
    </div>
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[600px]"
      leave-active-class="transition-all duration-300 ease-in"
      leave-from-class="opacity-100 max-h-[600px]"
      leave-to-class="opacity-0 max-h-0"
    >
      <div v-if="isMenuOpen" class="mt-4 lg:hidden overflow-hidden">
        <template v-for="link in links" :key="link.key">
          <!-- Mobile: dropdown parent expands inline -->
          <div v-if="link.isDropdown" class="py-1">
            <button
              type="button"
              @click="isMobileProgramsOpen = !isMobileProgramsOpen"
              class="w-full px-2 py-2 text-left rounded flex justify-between items-center font-semibold"
            >
              <span class="flex items-center gap-1.5">
                <Sparkles class="w-4 h-4 text-secondary shrink-0 talent-sparkle" />
                <span class="shiny-text">{{ $t(link.translationKey) }}</span>
              </span>
              <svg
                class="w-4 h-4 transition-transform duration-200"
                :class="{ 'rotate-180': isMobileProgramsOpen }"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            <div v-if="isMobileProgramsOpen" class="pl-4 mt-1 mb-2 space-y-1 border-l-2 border-secondary">
              <a
                v-for="item in programsSubmenu"
                :key="item.key"
                :href="item.link"
                class="flex items-center justify-between px-3 py-2 text-sm rounded hover:bg-primary hover:text-white"
              >
                <span>{{ $t(item.translationKey) }}</span>
                <span
                  v-if="item.isNew"
                  class="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-primary text-white"
                >
                  {{ $t('navbar.new') }}
                </span>
              </a>
            </div>
          </div>

          <!-- Mobile: regular link -->
          <div v-else class="block py-1">
            <a
              :href="link.link"
              class="block px-2 py-2 rounded hover:bg-primary hover:text-white transition-colors duration-100"
            >
              {{ $t(link.translationKey) }}
            </a>
          </div>
        </template>
      </div>
    </transition>
  </nav>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { Sparkles } from 'lucide-vue-next'
import LanguageSwitcher from './LanguageSwitcher.vue'

export default defineComponent({
  components: {
    LanguageSwitcher,
    Sparkles
  },
  setup() {
    const programsSubmenu = ref([
      { key: 'academy', translationKey: 'navbar.academy', link: '/dunk-and-kick', isNew: true },
      { key: 'nerd', translationKey: 'navbar.nerd', link: '/nerd-smart-afternoons', isNew: true },
      { key: 'talent', translationKey: 'navbar.talent', link: '/cdr-got-talent', isNew: true }
    ])

    const links = ref([
      { key: 'about', translationKey: 'navbar.about', link: '/#about' },
      { key: 'careers', translationKey: 'navbar.careers', link: '/careers' },
      { key: 'programs', translationKey: 'navbar.programs', isDropdown: true },
      { key: 'register', translationKey: 'navbar.register', link: '/registration' },
      { key: 'contact', translationKey: 'navbar.contact', link: '/#contact' }
    ])

    const isMenuOpen = ref(false)
    const isDropdownOpen = ref(false)
    const isMobileProgramsOpen = ref(false)
    let hideTimer: ReturnType<typeof setTimeout> | null = null

    const toggleMenu = () => {
      isMenuOpen.value = !isMenuOpen.value
    }

    const scheduleHideDropdown = () => {
      if (hideTimer) clearTimeout(hideTimer)
      hideTimer = setTimeout(() => {
        isDropdownOpen.value = false
      }, 150)
    }

    return {
      links,
      programsSubmenu,
      isMenuOpen,
      isDropdownOpen,
      isMobileProgramsOpen,
      toggleMenu,
      scheduleHideDropdown
    }
  }
})
</script>

<style scoped>
.shiny-text {
  background: linear-gradient(90deg, #D13732 0%, #FFD503 50%, #D13732 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 50%; }
  100% { background-position: -200% 50%; }
}

.talent-sparkle {
  animation: sparkle-pulse 1.6s ease-in-out infinite;
}

@keyframes sparkle-pulse {
  0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
  50% { transform: scale(1.2) rotate(15deg); opacity: 0.7; }
}
</style>
